import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Popover,
  TextField,
  Checkbox,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import { Search } from "lucide-react";

const MultiSelect = ({
  options = [],
  selectedValues = {},
  onSelectionChange,
  buttonLabel = "Select options",
  searchPlaceholder = "Search...",
  emptyMessage = "No options found",
  buttonIcon,
  getOptionLabel = option => option.label || option.name || String(option),
  getOptionId = option => option.id || option.key || String(option),
  searchFilter = (option, searchText) => {
    const label = getOptionLabel(option).toLowerCase();
    const id = getOptionId(option).toLowerCase();
    const searchLower = searchText.toLowerCase();
    return label.includes(searchLower) || id.includes(searchLower);
  },
  buttonProps = {},
  popoverProps = {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState("");

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText("");
  };

  const open = Boolean(anchorEl);

  // Filter options based on search text
  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) return options;
    return options.filter(option => searchFilter(option, searchText));
  }, [options, searchText, searchFilter]);

  // Calculate select all state
  const selectAllState = useMemo(() => {
    if (filteredOptions.length === 0) {
      return { checked: false, indeterminate: false };
    }

    const selectedCount = filteredOptions.filter(option => {
      const id = getOptionId(option);
      return selectedValues[id] !== false;
    }).length;

    const checked = selectedCount === filteredOptions.length;
    const indeterminate = selectedCount > 0 && selectedCount < filteredOptions.length;

    return { checked, indeterminate };
  }, [filteredOptions, selectedValues, getOptionId]);

  const handleToggle = id => {
    onSelectionChange?.(id);
  };

  const handleSelectAll = () => {
    const allSelected = selectAllState.checked;
    const shouldSelectAll = !allSelected; // If all are selected, deselect all; otherwise select all

    // Toggle all filtered options to the desired state
    filteredOptions.forEach(option => {
      const id = getOptionId(option);
      const isCurrentlySelected = selectedValues[id] !== false;
      // Only toggle if the current state doesn't match the desired state
      if (isCurrentlySelected !== shouldSelectAll) {
        onSelectionChange?.(id);
      }
    });
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={handleClick}
        startIcon={buttonIcon}
        sx={{
          textTransform: "none",
          borderColor: "#d1d5db",
          color: "#374151",
          "&:hover": {
            borderColor: "#9ca3af",
            backgroundColor: "#f9fafb",
          },
          ...buttonProps.sx,
        }}
        {...buttonProps}
      >
        {buttonLabel}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 250,
            maxWidth: 300,
            maxHeight: 400,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            ...popoverProps.PaperProps?.sx,
          },
          ...popoverProps.PaperProps,
        }}
        {...popoverProps}
      >
        <Box sx={{ p: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
          <List
            dense
            sx={{
              maxHeight: 300,
              overflow: "auto",
              p: 0,
            }}
          >
            {filteredOptions.length === 0 ? (
              <ListItem>
                <ListItemText primary={emptyMessage} sx={{ textAlign: "center", color: "#9ca3af" }} />
              </ListItem>
            ) : (
              <>
                {/* Select All option */}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleSelectAll}
                    dense
                    sx={{ py: 0.5, px: 1, borderBottom: "1px solid #e5e7eb" }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        checked={selectAllState.checked}
                        indeterminate={selectAllState.indeterminate}
                        size="small"
                        edge="start"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Select All"
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                {filteredOptions.map(option => {
                  const id = getOptionId(option);
                  const label = getOptionLabel(option);
                  const isSelected = selectedValues[id] !== false;

                  return (
                    <ListItem key={id} disablePadding>
                      <ListItemButton onClick={() => handleToggle(id)} dense sx={{ py: 0.5, px: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox checked={isSelected} size="small" edge="start" />
                        </ListItemIcon>
                        <ListItemText
                          primary={label}
                          primaryTypographyProps={{
                            fontSize: "0.875rem",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </>
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
};

MultiSelect.propTypes = {
  options: PropTypes.array.isRequired,
  selectedValues: PropTypes.object.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  buttonLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  emptyMessage: PropTypes.string,
  buttonIcon: PropTypes.node,
  getOptionLabel: PropTypes.func,
  getOptionId: PropTypes.func,
  searchFilter: PropTypes.func,
  buttonProps: PropTypes.object,
  popoverProps: PropTypes.object,
};

export default MultiSelect;
